import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { fmtError, fmtEUR, fmtDate } from "@/lib/api";
import { Sparkles, Calendar } from "lucide-react";

export default function StaffHomePage() {
  const { user } = useAuth();
  const { api, info } = useTenant();
  const firstName = (user?.name || "").split(" ")[0] || "Benvenuta";
  const [appts, setAppts] = useState([]);
  const [clientsCount, setClientsCount] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!api) return;
    Promise.all([api.allAppointments(), api.listClients()])
      .then(([a, c]) => { setAppts(a); setClientsCount(c.length); })
      .catch((e) => setError(fmtError(e)));
  }, [api]);

  const today = new Date().toDateString();
  const todayAppts = appts.filter((a) => new Date(a.starts_at).toDateString() === today);
  const completed = appts.filter((a) => a.status === "completed");
  const todayRevenue = completed
    .filter((a) => new Date(a.starts_at).toDateString() === today)
    .reduce((s, a) => s + (a.price || 0), 0);
  const totalPoints = completed.reduce((s, a) => s + 0, 0); // placeholder
  const nextAppts = appts
    .filter((a) => a.status === "booked" && new Date(a.starts_at) >= new Date())
    .sort((x, y) => new Date(x.starts_at) - new Date(y.starts_at))
    .slice(0, 5);

  return (
    <div data-testid="staff-welcome">
      <div className="text-[11px] tracking-[0.3em] uppercase text-[#A78BFA] mb-3">Area staff</div>
      <h1 className="font-serif font-light text-5xl md:text-6xl tracking-tight text-[#1A1A1A] leading-[1.02]">
        Buongiorno <span className="italic">{firstName}</span> <Sparkles size={28} className="inline -mt-2 text-[#A78BFA]" />
      </h1>
      <p className="mt-5 text-lg text-[#4A4A4A] font-light">Ecco la tua giornata in AURA.</p>

      {error && <div className="mt-4 text-sm text-[#B91C1C]">{error}</div>}

      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <KPI label="Appuntamenti oggi" value={todayAppts.length} testId="kpi-today" />
        <KPI label="Clienti totali" value={clientsCount} testId="kpi-clients" />
        <KPI label="Incasso oggi" value={fmtEUR(todayRevenue)} testId="kpi-revenue" />
        <KPI label="Completati totali" value={completed.length} testId="kpi-completed" />
      </div>

      <div className="mt-12 rounded-3xl border border-black/5 bg-white overflow-hidden" data-testid="staff-next-list">
        <div className="px-6 py-4 border-b border-black/5 flex items-center gap-2 text-[#1A1A1A]">
          <Calendar size={16} className="text-[#A78BFA]" />
          <span className="font-medium">Prossimi appuntamenti</span>
        </div>
        {nextAppts.length === 0 ? (
          <div className="p-8 text-center text-[#737373] font-light">Nessun appuntamento in arrivo.</div>
        ) : (
          <ul className="divide-y divide-black/5">
            {nextAppts.map((a) => (
              <li key={a.id} className="px-6 py-4 flex items-center gap-4">
                <div className="text-center min-w-[56px]">
                  <div className="font-serif text-2xl text-[#1A1A1A]">
                    {new Date(a.starts_at).toLocaleDateString("it-IT", { day: "2-digit" })}
                  </div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#737373]">
                    {new Date(a.starts_at).toLocaleDateString("it-IT", { month: "short" })}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="font-medium text-[#1A1A1A]">{a.service_name}</div>
                  <div className="text-xs text-[#737373]">{a.client_name} · {fmtDate(a.starts_at, info?.timezone)}</div>
                </div>
                <div className="font-medium text-[#1A1A1A]">{fmtEUR(a.price)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const KPI = ({ label, value, testId }) => (
  <div data-testid={testId} className="rounded-3xl border border-black/5 bg-white p-6 tilt-card">
    <div className="text-[10px] tracking-[0.25em] uppercase text-[#737373]">{label}</div>
    <div className="mt-3 font-serif font-light text-4xl text-[#1A1A1A] leading-none">{value}</div>
  </div>
);
